<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="products-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('products.title') }}</h1>
          <p class="page-subtitle">Manage your product catalog</p>
        </div>
        <div class="header-actions">
          <IntercomButton variant="secondary" @click="showStockModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <span>{{ $t('products.adjustStock') }}</span>
          </IntercomButton>
          <IntercomButton variant="primary" @click="showCreateModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('products.addProduct') }}</span>
            <kbd class="shortcut-hint">N</kbd>
          </IntercomButton>
        </div>
      </div>

      <!-- Search Bar with Sort & Scan -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <button class="scan-search-btn" @click="openSearchScanner" :title="$t('products.scanBarcode')">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <line x1="7" y1="12" x2="17" y2="12" />
            <line x1="7" y1="8" x2="13" y2="8" />
            <line x1="7" y1="16" x2="11" y2="16" />
          </svg>
        </button>
      </div>

      <!-- Products Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="products"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          hoverable
          clickable
          @row-click="handleEdit"
          @sort="handleSort"
        >
          <template #cell-barcode="{ value }">
            <span class="barcode-cell">{{ value || '-' }}</span>
          </template>

          <template #cell-tamil_name="{ value, row }">
            {{ row.localizations?.ta_IN || '-' }}
          </template>

          <template #cell-base_price="{ value }">
            ₹{{ parseFloat(value).toFixed(2) }}
          </template>

          <template #cell-stock_quantity="{ value, row }">
            <span :class="{ 'low-stock': row.min_stock_quantity > 0 && value <= row.min_stock_quantity }">
              {{ value }} {{ row.unit_type !== 'piece' ? row.unit_type : '' }}
              <span v-if="row.min_stock_quantity > 0 && value <= row.min_stock_quantity" class="low-stock-badge">Low</span>
            </span>
          </template>

          <template #cell-is_taxable="{ value }">
            <IntercomBadge :variant="value ? 'success' : 'default'">
              {{ value ? 'Yes' : 'No' }}
            </IntercomBadge>
          </template>

          <template #cell-gst_slab="{ row }">
            {{ row.is_taxable ? `${(row.cgst_rate || 0) + (row.sgst_rate || 0)}%` : '—' }}
          </template>

          <template #cell-created_at="{ value }">
            <span style="white-space: nowrap">{{ value ? formatDateTime(value) : '—' }}</span>
          </template>

          <template #cell-updated_at="{ value }">
            <span style="white-space: nowrap">{{ value ? formatDateTime(value) : '—' }}</span>
          </template>

          <template #cell-actions="{ row }">
            <div style="display: flex; gap: var(--space-2)">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleEdit(row)">
                {{ $t('common.edit') }}
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleDelete(row)">
                {{ $t('common.delete') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create/Edit Modal -->
      <ProductForm
        v-model:show="showCreateModal"
        :product="selectedProduct"
        @success="handleFormSuccess"
      />

      <!-- Stock Adjustment Modal -->
      <IntercomModal
        v-model:show="showStockModal"
        :title="$t('products.quickStockAdjustment')"
        width="500px"
      >
        <div class="stock-adjustment">
          <!-- Scan to find product -->
          <div class="stock-scan-section">
            <button class="stock-scan-btn" @click="openStockScanner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                <line x1="7" y1="12" x2="17" y2="12" />
              </svg>
              <span>{{ $t('products.scanBarcode') }}</span>
            </button>
            <span class="or-divider">{{ $t('common.or') }}</span>
            <IntercomSelect
              v-model="stockProductId"
              :options="stockProductOptions"
              placeholder="Select a product..."
              filterable
              class="stock-product-intercom"
            />
          </div>

          <!-- Selected Product Info -->
          <div v-if="stockProduct" class="stock-product-info">
            <div class="stock-product-name">{{ stockProduct.name }}</div>
            <div class="stock-product-details">
              <span>SKU: {{ stockProduct.sku }}</span>
              <span v-if="stockProduct.barcode">Barcode: {{ stockProduct.barcode }}</span>
            </div>
            <div class="stock-current">
              <span>{{ $t('products.currentStock') }}:</span>
              <strong>{{ stockProduct.stock_quantity }} {{ stockProduct.unit_type !== 'piece' ? stockProduct.unit_type : '' }}</strong>
            </div>
          </div>

          <!-- Adjustment Controls -->
          <div v-if="stockProduct" class="stock-controls">
            <div class="stock-adjustment-row">
              <label>{{ $t('products.adjustment') }}:</label>
              <div class="stock-adjust-input">
                <button class="stock-adjust-btn" @click="stockAdjustment = Math.max(-stockProduct.stock_quantity, stockAdjustment - 1)">-</button>
                <input v-model.number="stockAdjustment" type="number" class="stock-value-input" />
                <button class="stock-adjust-btn" @click="stockAdjustment++">+</button>
              </div>
            </div>

            <div class="stock-new-value">
              <span>{{ $t('products.newStock') }}:</span>
              <strong :class="{ 'text-danger': stockProduct.stock_quantity + stockAdjustment < 0 }">
                {{ Math.max(0, stockProduct.stock_quantity + stockAdjustment) }}
              </strong>
            </div>

            <IntercomSelect
              v-model="stockReason"
              :label="$t('products.reason')"
              :options="stockReasonOptions"
            />
          </div>
        </div>

        <template #footer>
          <IntercomButton variant="secondary" @click="closeStockModal">
            {{ $t('common.cancel') }}
          </IntercomButton>
          <IntercomButton
            variant="primary"
            :disabled="!stockProduct || stockAdjustment === 0"
            :loading="isUpdatingStock"
            @click="updateStock"
          >
            {{ $t('products.updateStock') }}
          </IntercomButton>
        </template>
      </IntercomModal>
    </div>

    <!-- Camera Scanner for Search -->
    <Teleport to="body">
      <CameraScanner
        v-if="showSearchScanner"
        :onScan="handleSearchScan"
        @close="closeSearchScanner"
        @scan="handleSearchScan"
      />
    </Teleport>

    <!-- Camera Scanner for Stock -->
    <Teleport to="body">
      <CameraScanner
        v-if="showStockScanner"
        :onScan="handleStockScan"
        @close="closeStockScanner"
        @scan="handleStockScan"
      />
    </Teleport>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomModal,
  IntercomPagination,
  IntercomSelect,
} from '@shared/components/intercom'
import ProductForm from '../components/ProductForm.vue'
import CameraScanner from '../components/CameraScanner.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDateTime } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { listProducts, deleteProduct, updateProduct, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: products,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listProducts(params))

const showCreateModal = ref(false)
const selectedProduct = ref(null)
const searchQuery = ref('')
const sortBy = ref('created_at')
const sortOrder = ref('desc')
const searchInputRef = ref(null)

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: products,
  onNew: () => { selectedProduct.value = null; showCreateModal.value = true },
  onOpen: (product) => handleEdit(product),
  onEdit: (product) => handleEdit(product),
  onDelete: (product) => handleDelete(product),
  searchRef: searchInputRef,
})

// Scanner states
const showSearchScanner = ref(false)
const showStockScanner = ref(false)

// Stock adjustment state
const showStockModal = ref(false)
const stockProduct = ref(null)
const stockAdjustment = ref(0)
const stockReason = ref('received')
const isUpdatingStock = ref(false)

// Stock product options derived from paginated products list
const stockProductOptions = computed(() =>
  products.value.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.sku})`,
  }))
)

// Computed writable: translate between stockProduct id ↔ full object
const stockProductId = computed({
  get: () => stockProduct.value?.id ?? null,
  set: (id) => {
    if (!id) {
      stockProduct.value = null
      return
    }
    stockProduct.value = products.value.find((p) => p.id === id) || null
    stockAdjustment.value = 0
  },
})

// Static reason options
const stockReasonOptions = [
  { value: 'received', label: 'Stock Received' },
  { value: 'sold', label: 'Sold' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'returned', label: 'Returned' },
  { value: 'correction', label: 'Correction' },
]

// Table columns - now includes barcode
const columns = [
  { key: 'sku', title: t('products.sku'), width: '100px' },
  { key: 'name', title: t('products.name'), width: '180px', sortable: true, sortKey: 'name' },
  { key: 'barcode', title: t('products.barcode'), width: '130px' },
  { key: 'tamil_name', title: t('products.tamilName'), width: '150px' },
  { key: 'base_price', title: t('products.basePrice'), width: '100px', align: 'right', sortable: true, sortKey: 'base_price' },
  { key: 'stock_quantity', title: t('products.stockQuantity'), width: '100px', align: 'right', sortable: true, sortKey: 'stock_quantity' },
  { key: 'is_taxable', title: t('products.isTaxable'), width: '80px' },
  { key: 'gst_slab', title: t('products.gstSlab', 'GST %'), width: '80px', align: 'right' },
  { key: 'created_at', title: 'Added', width: '140px', sortable: true, sortKey: 'created_at' },
  { key: 'updated_at', title: 'Updated', width: '140px', sortable: true, sortKey: 'updated_at' },
  { key: 'actions', title: 'Actions', width: '140px', align: 'right' },
]

const handleSearchInput = () => {
  resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value })
}

/**
 * Clear search
 */
const clearSearch = () => {
  searchQuery.value = ''
  resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value })
}

/**
 * Handle sort from IntercomTable
 */
const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ sort_by: newSort, sort_order: newOrder })
}

/**
 * Open search scanner
 */
const openSearchScanner = () => {
  showSearchScanner.value = true
}

/**
 * Close search scanner
 */
const closeSearchScanner = () => {
  showSearchScanner.value = false
}

/**
 * Handle search scan - find product by barcode
 */
const handleSearchScan = (barcode) => {
  closeSearchScanner()
  searchQuery.value = barcode
}

/**
 * Open stock scanner
 */
const openStockScanner = () => {
  showStockScanner.value = true
}

/**
 * Close stock scanner
 */
const closeStockScanner = () => {
  showStockScanner.value = false
}

/**
 * Handle stock scan - find product by barcode for stock adjustment
 */
const handleStockScan = (barcode) => {
  closeStockScanner()
  const found = products.value.find((p) => p.barcode === barcode)
  if (found) {
    stockProduct.value = found
    stockAdjustment.value = 0
  } else {
    toast.warning(t('products.barcodeNotFound', { barcode }))
  }
}

/**
 * Close stock modal
 */
const closeStockModal = () => {
  showStockModal.value = false
  stockProduct.value = null
  stockAdjustment.value = 0
  stockReason.value = 'received'
}

/**
 * Update stock
 */
const updateStock = async () => {
  if (!stockProduct.value || stockAdjustment.value === 0) return

  isUpdatingStock.value = true
  try {
    const newQuantity = Math.max(0, stockProduct.value.stock_quantity + stockAdjustment.value)
    await updateProduct(stockProduct.value.id, {
      ...stockProduct.value,
      stock_quantity: newQuantity,
    })

    // Update local data
    const index = products.value.findIndex((p) => p.id === stockProduct.value.id)
    if (index !== -1) {
      products.value[index].stock_quantity = newQuantity
    }

    closeStockModal()
    // TODO: Show success notification
  } catch (error) {
    console.error('Failed to update stock:', error)
    // TODO: Show error notification
  } finally {
    isUpdatingStock.value = false
  }
}

/**
 * Handle edit product
 */
const handleEdit = (product) => {
  selectedProduct.value = product
  showCreateModal.value = true
}

/**
 * Handle delete product
 */
const handleDelete = async (product) => {
  const ok = await confirm(`Delete product "${product.name}"?`, { title: 'Delete Product', type: 'error' })
  if (!ok) {
    return
  }

  try {
    await deleteProduct(product.id)
    await fetch()
  } catch (error) {
    console.error('Failed to delete product:', error)
  }
}

/**
 * Handle form success (create/update)
 */
const handleFormSuccess = async () => {
  showCreateModal.value = false
  selectedProduct.value = null
  await fetch()
}

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.products-view {
  padding: var(--space-8);
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Search Bar */
.search-bar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--color-gray-400);
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 44px;
  padding: 0 var(--space-10) 0 44px;
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  outline: none;
  transition: all var(--transition-fast);
}

.search-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
}

.search-clear {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-gray-100);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--color-gray-500);
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--color-gray-200);
  color: var(--color-gray-700);
}

.scan-search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: var(--color-blue);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.scan-search-btn:hover {
  background: var(--color-blue-dark, #0046cc);
  transform: scale(1.02);
}

/* Table Cells */
.barcode-cell {
  font-family: var(--font-mono);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
}

.low-stock {
  color: var(--color-red);
  font-weight: var(--font-weight-medium);
}

.low-stock-badge {
  display: inline-block;
  margin-left: var(--space-1);
  padding: 1px 6px;
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: white;
  background: var(--color-red);
  border-radius: 9px;
  vertical-align: middle;
}

/* Stock Adjustment Modal */
.stock-adjustment {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.stock-scan-section {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.stock-scan-btn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-blue);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
}

.stock-scan-btn:hover {
  background: var(--color-blue-dark, #0046cc);
}

.or-divider {
  color: var(--color-gray-400);
  font-size: var(--font-size-sm);
}

.stock-product-intercom {
  flex: 1;
}

.stock-product-info {
  background: var(--color-gray-50);
  border-radius: var(--radius-lg);
  padding: var(--space-4);
}

.stock-product-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin-bottom: var(--space-2);
}

.stock-product-details {
  display: flex;
  gap: var(--space-4);
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  margin-bottom: var(--space-3);
}

.stock-current {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--font-size-base);
}

.stock-current strong {
  font-size: var(--font-size-xl);
  color: var(--color-blue);
}

.stock-controls {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.stock-adjustment-row {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.stock-adjustment-row label {
  width: 100px;
  font-weight: var(--font-weight-medium);
}

.stock-adjust-input {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.stock-adjust-btn {
  width: 44px;
  height: 44px;
  background: var(--color-gray-50);
  border: none;
  cursor: pointer;
  font-size: var(--font-size-xl);
  color: var(--color-charcoal);
  transition: all var(--transition-fast);
}

.stock-adjust-btn:hover {
  background: var(--color-gray-100);
}

.stock-value-input {
  width: 80px;
  height: 44px;
  text-align: center;
  border: none;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.stock-value-input:focus {
  outline: none;
}

.stock-new-value {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-green-50, #f0fdf4);
  border-radius: var(--radius-md);
}

.stock-new-value strong {
  font-size: var(--font-size-xl);
  color: var(--color-green-600, #16a34a);
}

.stock-new-value .text-danger {
  color: var(--color-red) !important;
}

@media (max-width: 768px) {
  .products-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .page-title {
    font-size: 26px;
  }

  .search-bar {
    flex-direction: column;
  }

  .scan-search-btn {
    width: 100%;
  }

  .stock-scan-section {
    flex-direction: column;
  }
}

/* Shortcut hint styles are in shared/styles/intercom-base.css (.shortcut-hint, .search-shortcut-hint) */
</style>
